import { MEDIA_ACCEPT } from '@lixionary-jira/domain';
import { MediaPreview } from '../MediaPreview';
import { Download, Paperclip } from 'lucide-react';
import { useState } from 'react';
import { api } from '../../../api/client';
import { useAction } from '../../../api/hooks';
import { useIsAdmin, useMe } from '../../../api/session';
import type { TicketDetail } from '../../../api/ticket-types';
import { StateBadge, humanize } from '../../../ui/Badge';
import { Button } from '../../../ui/Button';
import { ErrorAlert } from '../../../ui/feedback';
import { ActorName, Card } from '../../../ui/misc';
import { Timestamp } from '../../../ui/Timestamp';
import { formatBytes } from '../bits';
import { DeleteOwn, ReasonDialog } from './dialogs';

export function FileChip({
  t,
  file,
}: {
  t: TicketDetail;
  file: { displayId: string; filename: string; scanState: string; sizeBytes: number };
}) {
  const href = `/api/v1/projects/${t.project.key}/tickets/${t.number}/attachments/${file.displayId}/download`;
  return (
    <span className="chip" style={{ height: 'auto', padding: '4px 10px', gap: 8 }}>
      <Paperclip size={12} aria-hidden />
      {file.scanState === 'clean' ? (
        <a href={href} download>
          {file.filename}
        </a>
      ) : (
        <span>{file.filename}</span>
      )}
      <span className="muted">{formatBytes(file.sizeBytes)}</span>
      {file.scanState !== 'clean' && (
        <StateBadge
          state={file.scanState}
          label={file.scanState === 'pending' ? 'Scanning' : humanize(file.scanState)}
        />
      )}
    </span>
  );
}

export function Files({ t }: { t: TicketDetail }) {
  const base = `/projects/${t.project.key}/tickets/${t.number}`;
  const admin = useIsAdmin();
  const me = useMe();
  const [removing, setRemoving] = useState<TicketDetail['attachments'][number] | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const upload = useAction(
    () => {
      const form = new FormData();
      form.append('file', file!);
      return api.upload(`${base}/attachments`, form);
    },
    {
      success: 'Uploaded',
      invalidate: [['ticket', t.project.key, t.number]],
      onSuccess: () => setFile(null),
    },
  );
  return (
    <Card
      title="Files"
      subtitle="Files follow the configured scan policy. Removed files keep their metadata."
    >
      {t.attachments.length === 0 ? (
        <span className="small muted">No files.</span>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>File</th>
                <th>Uploaded by</th>
                <th>State</th>
                <th className="num">Size</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {t.attachments.map((a) => (
                <tr key={a.id}>
                  <td>
                    {a.scanState === 'clean' && !a.removedAt ? (
                      <a
                        href={`/api/v1${base}/attachments/${a.displayId}/download`}
                        download
                        className="row nowrap"
                        style={{ gap: 6 }}
                      >
                        <Download size={13} aria-hidden /> {a.filename}
                      </a>
                    ) : (
                      <span className={a.removedAt ? 'muted' : ''}>{a.filename}</span>
                    )}
                    {a.scanState === 'clean' && !a.removedAt && (
                      <MediaPreview
                        src={`/api/v1${base}/attachments/${a.displayId}/preview`}
                        type={a.detectedType}
                        name={a.filename}
                      />
                    )}
                    <div className="mono small muted">
                      {a.displayId}
                      {a.removeReason ? ` · removed: ${a.removeReason}` : ''}
                    </div>
                  </td>
                  <td className="small">
                    <ActorName actor={a.uploader} /> · <Timestamp value={a.createdAt} dateOnly />
                  </td>
                  <td>
                    <StateBadge
                      state={a.scanState}
                      label={a.scanState === 'pending' ? 'Scanning' : undefined}
                    />
                  </td>
                  <td className="num">{formatBytes(a.sizeBytes)}</td>
                  <td>
                    {a.canRemove && !t.project.archived && (
                      <Button size="sm" variant="ghost" onClick={() => setRemoving(a)}>
                        Remove
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {t.viewer.canComment && (
        <form
          className="row"
          onSubmit={(e) => {
            e.preventDefault();
            upload.mutate(undefined);
          }}
        >
          <input
            type="file"
            aria-label="Choose file"
            accept={MEDIA_ACCEPT}
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
          <Button size="sm" type="submit" disabled={!file} loading={upload.isPending}>
            Upload
          </Button>
        </form>
      )}
      <ErrorAlert error={upload.error} />
      {removing &&
        (admin && removing.uploader.id !== me.user.id ? (
          <ReasonDialog
            title={`Remove ${removing.filename}?`}
            description="Content stops being downloadable; metadata and attribution remain. Files bound to evidence require rework first."
            confirm="Remove file"
            danger
            onClose={() => setRemoving(null)}
            run={(reason) =>
              api.del(`${base}/attachments/${removing.displayId}?reason=${encodeURIComponent(reason)}`)
            }
            invalidate={[['ticket', t.project.key, t.number]]}
          />
        ) : (
          <DeleteOwn
            onClose={() => setRemoving(null)}
            run={() => api.del(`${base}/attachments/${removing.displayId}`)}
            inv={[['ticket', t.project.key, t.number]]}
            me=""
          />
        ))}
    </Card>
  );
}
